import React, { useEffect, useRef, useState } from "react";
import "./LandingPageDashboard.css";
import { useNavigate } from "react-router-dom";
import {
  AuditOutlined,
  BarChartOutlined,
  BookOutlined,
  ContainerOutlined,
  TransactionOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import { onAuthStateChanged } from "firebase/auth";
import logo from "../Image/logo-svcc.png";
import ManageBook from "./ManageBook.js";
import BookReport from "./BookReport.js";
import AuditTrail from "./AuditTrail.js";
import ManageUser from "./ManageUser.js";
import Transaction from "./Transaction.js";
import LibraryMap from "./BookShelves/BookShelvesMap.js";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../Firebase/FirebaseConnection.js";
import {
  getAuth,
  reauthenticateWithCredential,
  updatePassword,
  EmailAuthProvider,
  sendPasswordResetEmail,
} from "firebase/auth";
import { message } from "antd";
import ReadingSession from "./ReadingSessions/ReadingSession.js";

const LandingPageDashboard = () => {
  const [activeSection, setActiveSection] = useState("manageBook");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const userIconRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        localStorage.clear();
        navigate("/");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        userIconRef.current &&
        !userIconRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("staff");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
        setEditableData(parsedUser);
      }
    } catch (error) {
      localStorage.removeItem("staff");
    }
  }, []);

  const handleButtonClick = (section) => {
    setActiveSection(section);
  };

  const handleUserIconClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleLogout = async () => {
    const auth = getAuth();
    await auth.signOut();
    localStorage.clear();
    navigate("/");
  };

  const handleAccountSettings = () => {
    setShowAccountModal(true);
  };

  const handleCloseModal = () => {
    setShowAccountModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableData({ ...editableData, [name]: value });
  };

  const handleSaveChanges = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      message.error("You must be logged in to update your account.");
      return;
    }

    if (!userData || !userData.id) {
      message.error("User data is missing.");
      return;
    }

    if (user.email !== userData.email) {
      message.error("You are not authorized to update this account.");
      return;
    }

    const userDocRef = doc(db, "staff", userData.id);
    setLoading(true);

    try {
      await updateDoc(userDocRef, editableData);

      localStorage.setItem(
        "staff",
        JSON.stringify({ id: userData.id, ...editableData })
      );

      let passwordUpdated = false;

      if (newPassword.trim() !== "" && confirmPassword.trim() !== "") {
        if (newPassword !== confirmPassword) {
          message.error("New passwords do not match.");
          setLoading(false);
          return;
        }

        if (newPassword.length < 6) {
          message.error("Password must be at least 6 characters long.");
          setLoading(false);
          return;
        }

        try {
          const credential = EmailAuthProvider.credential(
            user.email,
            currentPassword
          );
          await reauthenticateWithCredential(user, credential);
          await updatePassword(user, newPassword);

          message.success(
            "Password updated successfully! Please log in again."
          );
          passwordUpdated = true;
        } catch (error) {
          message.error(
            "Failed to update password. Please check your current password."
          );
          setLoading(false);
          return;
        }
      }

      message.success("Account information updated successfully!");

      if (passwordUpdated) {
        await auth.signOut();
        navigate("/");
      } else {
        setShowAccountModal(false);
      }
    } catch (error) {
      message.error("Failed to update information. Please try again");
    }

    setLoading(false);
  };

  const handleForgotPassword = async () => {
    const auth = getAuth();

    if (!userData || !userData.email) {
      message.error("Error: No email found for this account.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, userData.email);
      message.success("Password reset email sent! Please check your inbox.");
    } catch (error) {
      message.error("Failed to send password reset email. Try again later.");
    }
  };

  return (
    <div className="dashboard-main-container">
      <div className="sidebar">
        <div className="logo-container">
          <img className="avatar" src={logo} alt="Logo" />
          <div
            className="user-icon"
            ref={userIconRef}
            onClick={handleUserIconClick}
            onMouseEnter={() => setShowDropdown(true)}
            onMouseLeave={() => setShowDropdown(false)}
          >
            <SettingOutlined className="antd-user-icon" />
          </div>

          {showDropdown && (
            <div
              className={`user-dropdown ${showDropdown ? "show" : ""}`}
              ref={dropdownRef}
              onMouseEnter={() => setShowDropdown(true)}
              onMouseLeave={() => setShowDropdown(false)}
            >
              <div className="dropdown-item" onClick={handleAccountSettings}>
                <UserOutlined /> Account
              </div>
              <div className="dropdown-item" onClick={handleLogout}>
                <LogoutOutlined /> Logout
              </div>
            </div>
          )}
        </div>

        <div className="btn-sidebar">
          <button
            className={activeSection === "manageBook" ? "active" : ""}
            onClick={() => handleButtonClick("manageBook")}
          >
            <BookOutlined className="btn-side-icon" />
            Manage Book
          </button>
          <button
            className={activeSection === "manageUser" ? "active" : ""}
            onClick={() => handleButtonClick("manageUser")}
          >
            <UserOutlined className="btn-side-icon" />
            User Operation
          </button>
          <button
            className={activeSection === "transaction" ? "active" : ""}
            onClick={() => handleButtonClick("transaction")}
          >
            <TransactionOutlined className="btn-side-icon" />
            Book Operation
          </button>
          <button
            className={activeSection === "bookReport" ? "active" : ""}
            onClick={() => handleButtonClick("bookReport")}
          >
            <BarChartOutlined className="btn-side-icon" />
            Data Analytics
          </button>
          <button
            className={activeSection === "auditTrail" ? "active" : ""}
            onClick={() => handleButtonClick("auditTrail")}
          >
            <AuditOutlined className="btn-side-icon" />
            Audit Trail
          </button>
          <button
            className={activeSection === "ReadingSessions" ? "active" : ""}
            onClick={() => handleButtonClick("ReadingSessions")}
          >
            <ReadOutlined className="btn-side-icon" />
            Student Status
          </button>
          <button
            className={activeSection === "BookshelvesMap" ? "active" : ""}
            onClick={() => handleButtonClick("BookshelvesMap")}
          >
            <ContainerOutlined className="btn-side-icon" />
            Book Shelf Map
          </button>
        </div>
      </div>
      <div className="categories-container">
        {activeSection === "manageBook" && <ManageBook />}
        {activeSection === "manageUser" && <ManageUser />}
        {activeSection === "bookReport" && <BookReport />}
        {activeSection === "auditTrail" && <AuditTrail />}
        {activeSection === "transaction" && <Transaction />}
        {activeSection === "ReadingSessions" && <ReadingSession />}
        {activeSection === "BookshelvesMap" && <LibraryMap />}
      </div>
      {showAccountModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Account Settings</h2>

            <div className="modal-body">
              <div className="avatar-container">
                <div className="avatar-circle">
                  {editableData.name ? editableData.name[0] : "U"}
                </div>
                <p className="user-name">{editableData.name}</p>
                <p className="user-role">{editableData.designation}</p>
              </div>

              <div className="input-group">
                <label>Name:</label>
                <input
                  type="text"
                  name="name"
                  value={editableData.name || ""}
                  onChange={handleInputChange}
                />
              </div>

              <div className="input-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="email"
                  value={editableData.email || ""}
                  onChange={handleInputChange}
                />
              </div>

              <div className="input-group">
                <label>Phone Number:</label>
                <input
                  type="text"
                  name="number"
                  value={editableData.number || ""}
                  onChange={handleInputChange}
                />
              </div>

              {/* <div className="input-group">
                <label>Library Card Number:</label>
                <input
                  type="text"
                  name="libraryCardNumber"
                  value={editableData.libraryCardNumber || ""}
                  disabled
                />
              </div> */}

              <div className="input-group">
                <label>Designation:</label>
                <input
                  type="text"
                  name="designation"
                  value={editableData.designation || ""}
                  onChange={handleInputChange}
                />
              </div>

              <div className="input-group">
                <label>Gender:</label>
                <select
                  name="gender"
                  value={editableData.gender || ""}
                  onChange={handleInputChange}
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="input-group">
                <label>ID Number:</label>
                <input
                  type="text"
                  name="status"
                  value={editableData.staffId || ""}
                  disabled
                />
              </div>
              <div className="input-group">
                <label>Current Password:</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>

              <div className="input-group">
                <label>New Password:</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              </div>

              <div className="input-group">
                <label>Confirm New Password:</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>
              <div className="input-group">
                <button
                  className="forgot-password-btn"
                  onClick={handleForgotPassword}
                >
                  Reset your password using your registered email
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="save-btn"
                onClick={handleSaveChanges}
                disabled={loading}
              >
                {loading
                  ? "Updating..."
                  : newPassword.trim()
                  ? "Save & Change Password"
                  : "Save Changes"}
              </button>

              <button className="close-btn" onClick={handleCloseModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPageDashboard;
